import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { initUpload, getPartUrls, completeUpload, abortUpload, getPutUrl } from "../utils/multipartApi";

const UploadContext = createContext(null);

// regex pra remover surrogates soltos
const LONE_SURROGATE_RE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;
function sanitizeName(name) {
  if (!name) return name;
  return String(name).replace(LONE_SURROGATE_RE, "").normalize("NFC");
}

export function UploadProvider({ children }) {
  const [items, setItems] = useState([]);
  const fileRefMap   = useRef(new Map());
  const uploadIdMap  = useRef(new Map());
  const controllers  = useRef(new Map());
  const startedAtRef = useRef(new Map());
  const activeFiles  = useRef(0);
  const startedIds   = useRef(new Set());
  const canceledIds  = useRef(new Set());
  const finishedIds  = useRef(new Set());
  const queueRef     = useRef([]);
  const itemsRef     = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const MAX_CONCURRENT_FILES = 3;
  const MAX_PART_CONCURRENCY = 4;
  const HOTFIX_FORCE_DIRECT = true;
  const DIRECT_PUT_LIMIT      = 64 * 1024 * 1024;
  const COMPLETE_TIMEOUT_MS = 5 * 60 * 1000;

  const normPrefix = useCallback((p="") => (p ? (p.endsWith("/") ? p : p + "/") : ""), []);
  const withTimeout = (p, ms, msg="Timeout") => Promise.race([p, new Promise((_,rej)=>setTimeout(()=>rej(new Error(msg)), ms))]);

  const addFiles = useCallback((fileInput, bucketName, prefix = "") => {
    const list = Array.from(fileInput || []);
    if (!list.length) return;
    setItems(prev => {
      const existing = new Set(prev.map(p => `${p.name}|${p.size}`));
      const toAdd = [];
      for (const file of list) {
        if (existing.has(`${file.name}|${file.size}`)) continue;
        const id = uuidv4();
        const safeName = sanitizeName(file.name);
        // cria novo File com nome safe
        const safeFile = new File([file], safeName, { type: file.type });
        fileRefMap.current.set(id, safeFile);
        queueRef.current.push(id);
        toAdd.push({
          id,
          name: safeName,
          size: safeFile.size,
          type: safeFile.type || "application/octet-stream",
          bucket: bucketName,
          prefix: normPrefix(prefix),
          status: "queued",
          progress: 0,
          speedBps: 0,
          etaSec: null,
          error: null,
          displayName: file.name, // opcional: mostra o nome original na UI
        });
      }
      setTimeout(kick, 0);
      return [...prev, ...toAdd];
    });
  }, [normPrefix]);

  const cancelUpload = useCallback(async (id) => {
    const item = itemsRef.current.find(i => i.id === id);
    if (item?.status === "processing") return;
    canceledIds.current.add(id);
    finishedIds.current.add(id);
    queueRef.current = queueRef.current.filter(x => x !== id);
    const ctrls = controllers.current.get(id);
    try { ctrls?.file?.abort(); for (const c of ctrls?.parts?.values?.() || []) c.abort(); } catch {}
    controllers.current.delete(id);
    setItems(prev => prev.map(it => it.id === id ? { ...it, status: "canceled" } : it));
    const uploadId = uploadIdMap.current.get(id);
    if (uploadId && item) { try { await abortUpload({ bucket: item.bucket, prefix: item.prefix, name: item.name }, uploadId); } catch {} }
    startedAtRef.current.delete(id);
    const wasStarted = startedIds.current.has(id);
    if (!wasStarted) setTimeout(kick, 0);
  }, []);

  const clearUploads = useCallback(() => {
    for (const [, c] of controllers.current.entries()) {
      try { c.file?.abort(); for (const ctrl of c.parts.values()) ctrl.abort(); } catch {}
    }
    controllers.current.clear(); uploadIdMap.current.clear(); startedAtRef.current.clear();
    queueRef.current = []; startedIds.current.clear(); canceledIds.current.clear(); finishedIds.current.clear();
    activeFiles.current = 0; setItems([]);
  }, []);

  const clearFinished = useCallback(() => {
    setItems(prev => prev.filter(i => !["completed","canceled","error"].includes(i.status)));
  }, []);

  useEffect(() => {
    const hasActive = items.some(i => ["uploading","processing"].includes(i.status));
    const onBeforeUnload = (e) => { if (hasActive) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [items]);

  const kick = useCallback(() => {
    while (activeFiles.current < MAX_CONCURRENT_FILES && queueRef.current.length) {
      const id = queueRef.current.shift(); if (!id) break;
      if (startedIds.current.has(id) || finishedIds.current.has(id) || canceledIds.current.has(id)) continue;
      const item = itemsRef.current.find(i => i.id === id); if (!item) continue;
      startedIds.current.add(id); activeFiles.current++;
      setItems(prev => prev.map(it => it.id === id ? ({ ...it, status: "uploading" }) : it));
      uploadOne(item)
        .then(() => { if (!canceledIds.current.has(id)) { finishedIds.current.add(id); setItems(prev => prev.map(it => it.id === id ? ({ ...it, status: "completed", progress: 100 }) : it)); } })
        .catch(err => {
          const isAbort = err?.name === "AbortError" || canceledIds.current.has(id);
          if (!isAbort) { const msg = err?.message || String(err); finishedIds.current.add(id); setItems(prev => prev.map(it => it.id === id ? ({ ...it, status: "error", error: msg }) : it)); }
        })
        .finally(() => { startedIds.current.delete(id); activeFiles.current = Math.max(0, activeFiles.current - 1); startedAtRef.current.delete(id); setTimeout(kick, 0); });
    }
  }, []);

  const reportProgress = (id, uploadedBytes, totalBytes) => {
    if (canceledIds.current.has(id)) return;
    const now = performance.now();
    if (!startedAtRef.current.has(id)) startedAtRef.current.set(id, now);
    const start = startedAtRef.current.get(id);
    const elapsedSec = Math.max((now - start) / 1000, 0.5);
    const avgBps = uploadedBytes / elapsedSec;
    const left = Math.max(totalBytes - uploadedBytes, 0);
    const eta = avgBps > 0 ? Math.round(left / avgBps) : null;
    setItems(prev => prev.map(it => it.id === id
      ? { ...it, progress: Math.min(100, Math.round((uploadedBytes / totalBytes) * 100)), speedBps: Math.round(avgBps), etaSec: eta }
      : it
    ));
  };

  const getFile = (item) => fileRefMap.current.get(item.id) || null;
  const isCanceled = (id) => canceledIds.current.has(id);

  const uploadOne = async (item) => {
    if (isCanceled(item.id)) return;
    const file = getFile(item); if (!file) throw new Error("Arquivo não disponível.");
    controllers.current.set(item.id, { file: new AbortController(), parts: new Map() });
    startedAtRef.current.set(item.id, performance.now());

    if (file.size <= DIRECT_PUT_LIMIT) {
      try { await directPut(item, file); }
      catch (e) { console.warn("[upload] direct PUT falhou, caindo para MPU:", e?.message || e); await multipartUpload(item, file); }
    } else {
      try { await multipartUpload(item, file); }
      catch (e) { console.warn("[upload] MPU falhou, tentando direct PUT:", e?.message || e); await directPut(item, file); }
    }
    if (isCanceled(item.id)) return;
  };

  const directPut = async (item, file) => {
    const { url, headers } = await getPutUrl({ bucket: item.bucket, key: item.prefix + item.name, contentType: item.type || "application/octet-stream" });
    const ctrl = controllers.current.get(item.id).file;
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      xhr.timeout = 0;
      if (headers) for (const [k, v] of Object.entries(headers)) { try { xhr.setRequestHeader(k, v); } catch {} }
      xhr.upload.onprogress = (e) => reportProgress(item.id, e.loaded, e.total || file.size);
      xhr.onload  = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`HTTP ${xhr.status}`));
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
      const abortHandler = () => { try { xhr.abort(); } catch {} };
      ctrl.signal.addEventListener("abort", abortHandler, { once: true });
      try { xhr.send(file); } catch (err) { reject(err); }
    });
  };

  const multipartUpload = async (item, file) => {
    const resp = await initUpload({ bucket: item.bucket, prefix: item.prefix, name: item.name, type: item.type || "application/octet-stream", size: file.size });
    const uploadId = resp.uploadId;
    uploadIdMap.current.set(item.id, uploadId);

    const MIN_PART = 5 * 1024 * 1024;
    const partSize = Math.max(resp.partSize || MIN_PART, MIN_PART);
    const totalParts = Math.ceil(file.size / partSize);
    if (totalParts <= 1) { await directPut(item, file); return; }

    const maxPartsPerBatch = resp.maxPartsPerBatch || 1;
    const parts = [];
    let uploadedBytes = 0;
    const partQueue = Array.from({ length: totalParts }, (_, i) => i + 1);
    const inFlight = new Set();

    const fetchBatch = async (fromPn) => {
      const toPn = Math.min(fromPn + maxPartsPerBatch - 1, totalParts);
      const urls = await getPartUrls({ bucket: item.bucket, prefix: item.prefix, name: item.name }, uploadId, fromPn, toPn);
      const map = new Map(); for (const u of urls) map.set(u.partNumber, u.url);
      return map;
    };

    let urlCache = new Map();
    let scheduling = false;

    const schedule = async () => {
      if (scheduling) return; scheduling = true;
      try {
        while (inFlight.size < MAX_PART_CONCURRENCY && partQueue.length && !isCanceled(item.id)) {
          const partNumber = partQueue.shift();
          if (!urlCache.has(partNumber)) urlCache = await fetchBatch(partNumber);
          const url = urlCache.get(partNumber);
          const start = (partNumber - 1) * partSize;
          const end = Math.min(start + partSize, file.size);
          const blob = file.slice(start, end);

          const partCtrl = new AbortController();
          controllers.current.get(item.id).parts.set(partNumber, partCtrl);
          inFlight.add(partNumber);

          (async () => {
            try {
              const res = await fetch(url, { method: "PUT", body: blob, signal: partCtrl.signal });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const etag = (res.headers.get("ETag") || "").replaceAll('"', "");
              parts.push({ PartNumber: partNumber, ETag: etag });
              uploadedBytes += blob.size;
              reportProgress(item.id, uploadedBytes, file.size);
            } catch (err) {
              const aborted = err?.name === "AbortError" || isCanceled(item.id);
              if (!aborted) partQueue.unshift(partNumber);
            } finally {
              inFlight.delete(partNumber);
              controllers.current.get(item.id)?.parts?.delete(partNumber);
              setTimeout(schedule, 0);
            }
          })();
        }
      } finally { scheduling = false; }
    };

    await schedule();
    while (!isCanceled(item.id) && (inFlight.size > 0 || partQueue.length > 0)) await new Promise(r => setTimeout(r, 30));

    if (!isCanceled(item.id)) {
      parts.sort((a, b) => a.PartNumber - b.PartNumber);
      setItems(prev => prev.map(it => it.id === item.id ? { ...it, status: "processing", progress: 100 } : it));
      await withTimeout(completeUpload({ bucket: item.bucket, prefix: item.prefix, name: item.name }, uploadId, parts), COMPLETE_TIMEOUT_MS, "Timeout ao finalizar upload");
    }
  };

  return (
    <UploadContext.Provider value={{ items, addFiles, cancelUpload, clearUploads, clearFinished }}>
      {children}
    </UploadContext.Provider>
  );
}

export function useUploadContext() {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error("useUploadContext must be used within UploadProvider");
  return ctx;
}
